import { defineStore } from "pinia";
import { withErrorHandling } from "~/helpers/withErrorHandling";
import { withErrorMessage } from "~/helpers/withErrorMessage";
import { withLoading } from "~/helpers/withLoading";
import type { WebModelGetUserAppSubscription } from "~/model/api/pLCnextStoreFrontendAPI.schemas";
import {
  addAppSafetySubscription,
  addAppUpdateSubscription,
  getAppInfoByUnsubscribeToken,
  getAppSubscriptions,
  removeAppSafetySubscription,
  removeAppUpdateSubscription,
  removeAppUpdateSubscriptionByToken,
} from "~/model/api/pLCnextStoreFrontendAPI";

interface State {
  userAppSubscriptions: WebModelGetUserAppSubscription[];
}

export const useUserAppSubscriptionsStore = defineStore(
  "userAppSubscriptions",
  {
    state: (): State => ({
      userAppSubscriptions: [], // initial state
    }),
    getters: {},
    actions: {
      async loadUserAppSubscriptions(userUuid: string) {
        const { data, error } = await withErrorHandling(
          withErrorMessage(withLoading(getAppSubscriptions(userUuid))),
        );
        console.debug("loadUserAppSubscriptions: ", data, error);
        if (data) {
          this.userAppSubscriptions = data;
        }
      },
      async addUserAppSubscription(appId: number): Promise<boolean> {
        const userStore = useUserStore();
        console.debug("addUserAppSubscription: ", appId);
        if (!userStore.authUserUuid) {
          console.error("No user uuid found in user store");
          return false;
        }
        const { data } = await withErrorMessage(
          withLoading(addAppUpdateSubscription(userStore.authUserUuid, appId)),
        );
        console.debug("addAppSubscription: ", data);
        await this.loadUserAppSubscriptions(userStore.authUserUuid);
        return true;
      },
      async addAppSafetySubscription(appId: number): Promise<boolean> {
        const userStore = useUserStore();
        console.debug("addAppSafetySubscription: ", appId);
        if (!userStore.authUserUuid) {
          console.error("No user uuid found in user store");
          return false;
        }
        const { data } = await withErrorMessage(
          withLoading(addAppSafetySubscription(userStore.authUserUuid, appId)),
        );
        console.debug("addAppSafetySubscription: ", data);
        await this.loadUserAppSubscriptions(userStore.authUserUuid);
        return true;
      },
      async removeUserAppSubscription(appId: number): Promise<boolean> {
        const userStore = useUserStore();
        console.debug("removeUserAppSubscription: ", appId);
        if (!userStore.authUserUuid) {
          console.error("No user uuid found in user store");
          return false;
        }
        const { data } = await withErrorMessage(
          withLoading(
            removeAppUpdateSubscription(userStore.authUserUuid, appId),
          ),
        );
        console.debug("removeUserAppSubscription: ", data);
        await this.loadUserAppSubscriptions(userStore.authUserUuid);
        return true;
      },
      async removeUserAppSafetySubscription(appId: number): Promise<boolean> {
        const userStore = useUserStore();
        console.debug("removeUserAppSafetySubscription: ", appId);
        if (!userStore.authUserUuid) {
          console.error("No user uuid found in user store");
          return false;
        }
        const { data } = await withErrorMessage(
          withLoading(
            removeAppSafetySubscription(userStore.authUserUuid, appId),
          ),
        );
        console.debug("removeUserAppSafetySubscription: ", data);
        await this.loadUserAppSubscriptions(userStore.authUserUuid);
        return true;
      },
      async loadUserAppSubscriptionsForToken(token: string) {
        const { data, error } = await withErrorHandling(
          withErrorMessage(withLoading(getAppInfoByUnsubscribeToken(token))),
        );
        console.debug("loadUserAppSubscriptionsForToken: ", data, error);
        if (data) {
          return data;
        }
        return null;
      },
      async removeUserAppSubscriptionForToken(token: string) {
        const { data } = await withErrorMessage(
          withLoading(removeAppUpdateSubscriptionByToken(token)),
        );
        console.debug("removeUserAppSubscriptionForToken: ", data);
        return true;
      },
      clear() {
        this.$reset();
      },
    },
  },
);
